import { useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { isApiError } from '@/lib/api-errors';
import { adoptSession, type SessionPayload } from '@/lib/session-adopt';
import { cn } from '@/utils/cn';

type QrStatus = 'pending' | 'scanned' | 'authenticated' | 'expired' | 'cancelled' | 'error';

interface QrSession {
  sessionToken: string;
  qrCodeData: string;
  expiresAt: string;
}

const QR_SIZE = 160;
/** Time allowed to approve on the phone after a scan before the console shows a new code. */
const APPROVAL_WAIT_SECONDS = 120;

export const QrLoginPanel = ({ destination }: { destination: string }) => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [status, setStatus] = useState<QrStatus>('pending');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [approvalCountdown, setApprovalCountdown] = useState(0);
  const consumeStartedRef = useRef(false);
  const scannedAtRef = useRef<number | null>(null);
  const approvalTimedOutRef = useRef(false);
  const statusRef = useRef<QrStatus>(status);
  statusRef.current = status;

  const {
    mutate: consumeSession,
    isPending: isConsuming,
    error: consumeError,
    reset: resetConsume,
  } = useMutation({
    mutationFn: (token: string) =>
      api.post<SessionPayload>('/auth/qr-login/consume', { sessionToken: token }),
    onSuccess: (payload) => {
      const user = adoptSession(payload);
      if (user.platformRole !== 'platform_admin' && user.platformRole !== 'platform_manager') {
        setError(t('signIn.accessDenied'));
        consumeStartedRef.current = false;
        return;
      }
      void navigate(destination, { replace: true });
    },
    onError: () => {
      consumeStartedRef.current = false;
    },
  });

  const requestSession = useCallback(async () => {
    setError(null);
    setStatus('pending');
    setLoading(true);
    setQrCodeData(null);
    setSessionToken(null);
    setExpiresAt(null);
    setApprovalCountdown(0);
    consumeStartedRef.current = false;
    scannedAtRef.current = null;
    approvalTimedOutRef.current = false;
    resetConsume();

    try {
      const session = await api.post<QrSession>('/auth/qr-login/request', {});
      setSessionToken(session.sessionToken);
      setQrCodeData(session.qrCodeData);
      setExpiresAt(new Date(session.expiresAt));
    } catch (err) {
      setError(
        isApiError(err)
          ? tErrors(err.code, err.params)
          : t('qrLogin.requestFailed'),
      );
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [resetConsume, t, tErrors]);

  const cancelSession = useCallback(async (token: string) => {
    try {
      await api.post(`/auth/qr-login/cancel/${encodeURIComponent(token)}`, {});
    } catch {
      // Best-effort — a new session request still works if this fails.
    }
  }, []);

  const handleApprovalTimeout = useCallback(async () => {
    if (approvalTimedOutRef.current || !sessionToken || statusRef.current !== 'scanned') {
      return;
    }
    approvalTimedOutRef.current = true;
    await cancelSession(sessionToken);
    void requestSession();
  }, [cancelSession, requestSession, sessionToken]);

  useEffect(() => {
    void requestSession();
  }, [requestSession]);

  useEffect(() => {
    if (!qrCodeData || !canvasRef.current || status !== 'pending') return;

    void QRCode.toCanvas(canvasRef.current, qrCodeData, {
      width: QR_SIZE,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    });
  }, [qrCodeData, status]);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0 && (status === 'pending' || status === 'scanned')) setStatus('expired');
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt, status]);

  useEffect(() => {
    if (status !== 'scanned' || scannedAtRef.current == null) {
      setApprovalCountdown(0);
      return;
    }

    const deadline = scannedAtRef.current + APPROVAL_WAIT_SECONDS * 1000;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
      setApprovalCountdown(remaining);
      if (remaining === 0) void handleApprovalTimeout();
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [handleApprovalTimeout, status]);

  useEffect(() => {
    if (
      !sessionToken ||
      status === 'expired' ||
      status === 'error' ||
      status === 'cancelled'
    ) {
      return;
    }
    if (status === 'authenticated' && consumeStartedRef.current) {
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      try {
        const result = await api.get<{ status: QrStatus; expiresAt: string }>(
          `/auth/qr-login/status/${encodeURIComponent(sessionToken)}`,
        );
        if (cancelled) return;

        if (result.status === 'scanned' && scannedAtRef.current == null) {
          scannedAtRef.current = Date.now();
        }

        setStatus(result.status);
        if (result.status === 'authenticated' && !consumeStartedRef.current) {
          consumeStartedRef.current = true;
          consumeSession(sessionToken);
        }
      } catch {
        // Transient network errors should not reset the QR.
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), 2000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [consumeSession, sessionToken, status]);

  const isAwaitingApproval = status === 'scanned';
  const isLocked =
    isAwaitingApproval || status === 'authenticated' || isConsuming || status === 'cancelled';

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const resolveConsumeError = (err: unknown) => {
    if (err instanceof ApiError) return tErrors(err.code, err.params);
    return t('qrLogin.consumeFailed');
  };

  return (
    <div className="flex w-full flex-col items-center gap-4 text-center">
      <div className="w-full space-y-1 text-center">
        <p className="text-body font-semibold text-fg">{t('qrLogin.title')}</p>
        <p className="text-body-sm leading-snug text-fg-secondary">{t('qrLogin.description')}</p>
      </div>

      {loading ? (
        <div
          className="flex flex-col items-center justify-center gap-2"
          style={{ minHeight: QR_SIZE + 8 }}
        >
          <Spinner className="size-5 text-brand" />
          <p className="text-body-sm text-fg-tertiary">{t('qrLogin.loading')}</p>
        </div>
      ) : status === 'error' ? (
        <div
          className="flex flex-col items-center justify-center gap-2"
          style={{ minHeight: QR_SIZE + 8 }}
        >
          <p className="text-body-sm text-danger">{error}</p>
          <Button variant="link" size="sm" onClick={() => void requestSession()}>
            {t('qrLogin.refresh')}
          </Button>
        </div>
      ) : (
        <>
          <div
            className="relative rounded-lg bg-white p-1.5 shadow-sm"
            style={{ minHeight: QR_SIZE + 8, minWidth: QR_SIZE + 8 }}
          >
            {isLocked ? (
              <div
                className="flex flex-col items-center justify-center gap-2 rounded-sm px-3 text-center"
                style={{ width: QR_SIZE, minHeight: QR_SIZE }}
              >
                {status === 'cancelled' ? (
                  <button
                    type="button"
                    className="flex flex-col items-center justify-center gap-1.5 text-caption"
                    onClick={() => void requestSession()}
                  >
                    <span className="font-medium text-fg">{t('qrLogin.declinedTitle')}</span>
                    <span className="text-body-sm text-fg-secondary">{t('qrLogin.declined')}</span>
                    <span className="text-brand underline">{t('qrLogin.refresh')}</span>
                  </button>
                ) : (
                  <>
                    <Spinner className="size-6 text-brand" />
                    <p className="text-body-sm font-medium text-fg">
                      {isAwaitingApproval ? t('qrLogin.scanned') : t('qrLogin.signingIn')}
                    </p>
                    {isAwaitingApproval ? (
                      <p className="text-body-sm text-fg-secondary">{t('qrLogin.waitingApproval')}</p>
                    ) : null}
                    {isAwaitingApproval && approvalCountdown > 0 ? (
                      <p className="text-caption text-fg-tertiary">
                        {t('qrLogin.approvalExpiresIn', { time: formatTime(approvalCountdown) })}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ) : (
              <>
                <canvas
                  ref={canvasRef}
                  className={cn(
                    'rounded-sm',
                    status === 'expired' && 'opacity-40 blur-[2px] grayscale',
                  )}
                  aria-hidden={status === 'expired'}
                />
                {status === 'expired' ? (
                  <button
                    type="button"
                    className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-white/85 text-caption"
                    onClick={() => void requestSession()}
                  >
                    <span className="font-medium text-fg">{t('qrLogin.expiredTitle')}</span>
                    <span className="text-brand underline">{t('qrLogin.refresh')}</span>
                  </button>
                ) : null}
              </>
            )}
          </div>

          {status === 'pending' && countdown > 0 ? (
            <p className="text-body-sm text-fg-tertiary">
              {t('qrLogin.expiresIn', { time: formatTime(countdown) })}
            </p>
          ) : null}

          {status === 'pending' ? (
            <p className="max-w-[11rem] text-body-sm leading-snug text-fg-tertiary">
              {t('qrLogin.scanHint')}
            </p>
          ) : null}

          {consumeError ? (
            <div className="space-y-2">
              <p className="text-body-sm text-danger">{resolveConsumeError(consumeError)}</p>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  consumeStartedRef.current = false;
                  resetConsume();
                  consumeSession(sessionToken!);
                }}
              >
                {t('qrLogin.retrySignIn')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
